import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
const Privacy = () => {
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-480">
      {/* Header */}
      <header className="p-4 bg-white shadow-sm" data-id="element-481">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center" data-id="element-482">
          <Link to="/" className="text-burgundy font-display text-2xl font-bold" data-id="element-483">
            Chef<span className="text-sage" data-id="element-484">Chat</span>
          </Link>
          <Link to="/" className="text-charcoal hover:text-burgundy flex items-center text-sm font-medium" data-id="element-485">
            <ArrowLeftIcon size={16} className="mr-1" data-id="element-486" />
            Back to home
          </Link>
        </div>
      </header>
      {/* Main content */}
      <main className="flex-grow py-12 px-4" data-id="element-487">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8" data-id="element-488">
          <h1 className="text-3xl md:text-4xl font-bold text-charcoal font-display mb-4" data-id="element-489">
            Privacy Policy
          </h1>
          <p className="text-sage mb-8" data-id="element-490">
            Last updated: {new Date().toLocaleDateString()}
          </p>
          <div className="prose prose-lg max-w-none text-charcoal" data-id="element-491">
            <section className="mb-8" data-id="element-492">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-493">
                1. Introduction
              </h2>
              <p className="mb-4" data-id="element-494">
                ChefChat ("we," "our," or "us") is committed to protecting your
                privacy and the privacy of your customers. This Privacy Policy
                explains how we collect, use, disclose, and safeguard your
                information when you use our AI-powered customer communication
                management Service.
              </p>
              <p className="mb-4" data-id="element-495">
                We understand that as a restaurant owner, you're entrusting us
                with sensitive business information and customer communications.
                We take this responsibility seriously and are committed to
                transparency about our data practices.
              </p>
            </section>
            <section className="mb-8" data-id="element-496">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-497">
                2. Information We Collect
              </h2>
              <h3 className="text-xl font-semibold text-charcoal mb-3" data-id="element-498">
                Restaurant Information
              </h3>
              <p className="mb-4" data-id="element-499">
                To provide our Service, we collect information about your
                restaurant, including:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-500">
                <li data-id="element-501">Restaurant name, location, and contact information</li>
                <li data-id="element-502">Hours of operation and holiday schedules</li>
                <li data-id="element-503">Menu items, descriptions, and pricing</li>
                <li data-id="element-504">Dietary options and allergen information</li>
                <li data-id="element-505">Reservation policies and capacity</li>
                <li data-id="element-506">Special services and amenities</li>
                <li data-id="element-507">Brand voice guidelines and communication preferences</li>
              </ul>
              <h3 className="text-xl font-semibold text-charcoal mb-3" data-id="element-508">
                Account and User Information
              </h3>
              <p className="mb-4" data-id="element-509">
                We collect information about you as the account holder:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-510">
                <li data-id="element-511">Name and email address</li>
                <li data-id="element-512">Account credentials</li>
                <li data-id="element-513">
                  Payment information (processed securely through third-party
                  payment processors)
                </li>
                <li data-id="element-514">Communication preferences</li>
                <li data-id="element-515">Role and permissions within your organization</li>
              </ul>
              <h3 className="text-xl font-semibold text-charcoal mb-3" data-id="element-516">
                Customer Communication Data
              </h3>
              <p className="mb-4" data-id="element-517">
                To provide AI-powered responses, we collect and process:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-518">
                <li data-id="element-519">
                  Customer messages and inquiries from connected platforms
                </li>
                <li data-id="element-520">
                  Social media interactions (comments, direct messages, reviews)
                </li>
                <li data-id="element-521">Customer sentiment and feedback</li>
                <li data-id="element-522">Reservation requests and booking information</li>
                <li data-id="element-523">Response history and conversation threads</li>
              </ul>
              <h3 className="text-xl font-semibold text-charcoal mb-3" data-id="element-524">
                Platform Connection Data
              </h3>
              <p className="mb-4" data-id="element-525">
                When you connect social media and review platforms:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-526">
                <li data-id="element-527">OAuth tokens and platform access credentials</li>
                <li data-id="element-528">Platform-specific account information</li>
                <li data-id="element-529">Page or business profile data</li>
                <li data-id="element-530">Historical message and review data</li>
              </ul>
              <h3 className="text-xl font-semibold text-charcoal mb-3" data-id="element-531">
                Usage and Analytics Information
              </h3>
              <p className="mb-4" data-id="element-532">
                We automatically collect information about how you use our
                Service:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-533">
                <li data-id="element-534">Device information (browser type, operating system)</li>
                <li data-id="element-535">IP address and general location data</li>
                <li data-id="element-536">Usage patterns and feature interactions</li>
                <li data-id="element-537">Response approval rates and modifications</li>
                <li data-id="element-538">Service performance metrics</li>
                <li data-id="element-539">Cookies and similar tracking technologies</li>
              </ul>
            </section>
            <section className="mb-8" data-id="element-540">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-541">
                3. How We Use Your Information
              </h2>
              <p className="mb-4" data-id="element-542">We use the information we collect to:</p>
              <ul className="list-disc pl-6 mb-4" data-id="element-543">
                <li data-id="element-544">
                  Generate AI-powered responses to customer inquiries in your
                  restaurant's authentic voice
                </li>
                <li data-id="element-545">
                  Manage customer communications across multiple platforms
                </li>
                <li data-id="element-546">
                  Learn and improve response quality through your feedback and
                  approvals
                </li>
                <li data-id="element-547">
                  Provide analytics and insights about customer interactions
                </li>
                <li data-id="element-548">Process your transactions and manage your subscription</li>
                <li data-id="element-549">
                  Send you service updates, notifications, and important account
                  information
                </li>
                <li data-id="element-550">Respond to your support requests and questions</li>
                <li data-id="element-551">
                  Improve our AI models and Service quality through anonymized
                  data
                </li>
                <li data-id="element-552">Monitor and analyze usage trends and preferences</li>
                <li data-id="element-553">
                  Detect, prevent, and address technical issues and security
                  threats
                </li>
                <li data-id="element-554">Comply with legal obligations</li>
              </ul>
            </section>
            <section className="mb-8" data-id="element-555">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-556">
                4. Information Sharing and Disclosure
              </h2>
              <p className="mb-4" data-id="element-557">
                We do not sell your personal information or customer data. We
                may share information in the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-558">
                <li data-id="element-559">
                  <strong data-id="element-560">Service Providers:</strong> With trusted third-party
                  vendors who perform services on our behalf (cloud hosting,
                  payment processing, analytics). These providers are
                  contractually obligated to protect your data.
                </li>
                <li data-id="element-561">
                  <strong data-id="element-562">Connected Platforms:</strong> With social media and
                  review platforms you've authorized us to access, solely to
                  post approved responses on your behalf.
                </li>
                <li data-id="element-563">
                  <strong data-id="element-564">Legal Requirements:</strong> When required by law,
                  court order, or to protect our rights, safety, or the safety
                  of others.
                </li>
                <li data-id="element-565">
                  <strong data-id="element-566">Business Transfers:</strong> In connection with a
                  merger, acquisition, or sale of assets, with appropriate
                  safeguards for your data.
                </li>
                <li data-id="element-567">
                  <strong data-id="element-568">With Your Consent:</strong> When you explicitly
                  authorize us to share your information.
                </li>
              </ul>
              <p className="mb-4" data-id="element-569">
                <strong data-id="element-570">Important:</strong> We use anonymized, aggregated data
                to improve our AI models, but this data cannot be traced back to
                your specific restaurant or customers.
              </p>
            </section>
            <section className="mb-8" data-id="element-571">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-572">
                5. Data Security
              </h2>
              <p className="mb-4" data-id="element-573">
                We implement industry-standard technical and organizational
                measures to protect your information:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-574">
                <li data-id="element-575">Encryption of data in transit and at rest</li>
                <li data-id="element-576">
                  Secure cloud infrastructure with regular security audits
                </li>
                <li data-id="element-577">Access controls and authentication requirements</li>
                <li data-id="element-578">Regular security assessments and updates</li>
                <li data-id="element-579">Employee training on data protection practices</li>
              </ul>
              <p className="mb-4" data-id="element-580">
                However, no method of transmission over the Internet is 100%
                secure. While we strive to protect your information, we cannot
                guarantee absolute security.
              </p>
            </section>
            <section className="mb-8" data-id="element-581">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-582">
                6. Customer Data and Privacy
              </h2>
              <p className="mb-4" data-id="element-583">
                We recognize that customer communications contain personal
                information belonging to your patrons. We process this data
                solely to provide our Service to you and never use it for any
                other purpose.
              </p>
              <p className="mb-4" data-id="element-584">
                You are responsible for complying with applicable privacy laws
                regarding your customers' data, including obtaining necessary
                consents and providing required privacy notices.
              </p>
            </section>
            <section className="mb-8" data-id="element-585">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-586">
                7. Data Retention
              </h2>
              <p className="mb-4" data-id="element-587">
                We retain your information for as long as necessary to provide
                our Service and fulfill the purposes outlined in this Privacy
                Policy:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-588">
                <li data-id="element-589">
                  <strong data-id="element-590">Active Account Data:</strong> Maintained while your
                  account is active
                </li>
                <li data-id="element-591">
                  <strong data-id="element-592">Communication History:</strong> Retained to improve AI
                  responses and provide historical context
                </li>
                <li data-id="element-593">
                  <strong data-id="element-594">After Account Closure:</strong> Data is retained for
                  180 days to allow for account recovery, then securely deleted
                </li>
                <li data-id="element-595">
                  <strong data-id="element-596">Legal Requirements:</strong> Some data may be retained
                  longer if required by law
                </li>
              </ul>
              <p className="mb-4" data-id="element-597">
                You can request deletion of your data at any time by contacting
                us at privacy[at]getchefchat.com.
              </p>
            </section>
            <section className="mb-8" data-id="element-598">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-599">
                8. Your Rights and Choices
              </h2>
              <p className="mb-4" data-id="element-600">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-601">
                <li data-id="element-602">
                  <strong data-id="element-603">Access:</strong> Request access to your personal
                  information and data we hold
                </li>
                <li data-id="element-604">
                  <strong data-id="element-605">Correction:</strong> Request correction of inaccurate
                  information
                </li>
                <li data-id="element-606">
                  <strong data-id="element-607">Deletion:</strong> Request deletion of your personal
                  information and restaurant data
                </li>
                <li data-id="element-608">
                  <strong data-id="element-609">Data Portability:</strong> Request a copy of your
                  information in a portable format
                </li>
                <li data-id="element-610">
                  <strong data-id="element-611">Opt-Out:</strong> Unsubscribe from marketing
                  communications (service-related communications will continue)
                </li>
                <li data-id="element-612">
                  <strong data-id="element-613">Restrict Processing:</strong> Request limitation of
                  how we process your data
                </li>
                <li data-id="element-614">
                  <strong data-id="element-615">Withdraw Consent:</strong> Revoke consent for data
                  processing where applicable
                </li>
                <li data-id="element-616">
                  <strong data-id="element-617">Platform Disconnection:</strong> Disconnect social
                  media and review platforms at any time
                </li>
              </ul>
              <p className="mb-4" data-id="element-618">
                To exercise these rights, please contact us at
                privacy[at]getchefchat.com. We will respond to your request
                within 30 days.
              </p>
            </section>
            <section className="mb-8" data-id="element-619">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-620">
                9. Cookies and Tracking Technologies
              </h2>
              <p className="mb-4" data-id="element-621">
                We use cookies and similar technologies to enhance your
                experience, analyze usage patterns, and improve our Service:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-622">
                <li data-id="element-623">
                  <strong data-id="element-624">Essential Cookies:</strong> Required for basic Service
                  functionality
                </li>
                <li data-id="element-625">
                  <strong data-id="element-626">Analytics Cookies:</strong> Help us understand how you
                  use the Service
                </li>
                <li data-id="element-627">
                  <strong data-id="element-628">Preference Cookies:</strong> Remember your settings
                  and choices
                </li>
              </ul>
              <p className="mb-4" data-id="element-629">
                You can control cookies through your browser settings, but
                disabling certain cookies may limit Service functionality.
              </p>
            </section>
            <section className="mb-8" data-id="element-630">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-631">
                10. Third-Party Platforms
              </h2>
              <p className="mb-4" data-id="element-632">
                Our Service integrates with third-party platforms (Facebook,
                Instagram, Google, Yelp, etc.). These platforms have their own
                privacy policies governing the data they collect and how they
                use it. We encourage you to review their privacy policies.
              </p>
              <p className="mb-4" data-id="element-633">
                ChefChat is not responsible for the privacy practices of these
                external platforms.
              </p>
            </section>
            <section className="mb-8" data-id="element-634">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-635">
                11. International Data Transfers
              </h2>
              <p className="mb-4" data-id="element-636">
                Your information may be transferred to and processed in
                countries other than your country of residence. We ensure
                appropriate safeguards are in place to protect your information
                in accordance with this Privacy Policy and applicable laws.
              </p>
            </section>
            <section className="mb-8" data-id="element-637">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-638">
                12. Changes to This Privacy Policy
              </h2>
              <p className="mb-4" data-id="element-639">
                We may update this Privacy Policy from time to time to reflect
                changes in our practices or legal requirements. We will notify
                you of any material changes by:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-640">
                <li data-id="element-641">Posting the updated policy on this page</li>
                <li data-id="element-642">Updating the "Last updated" date</li>
                <li data-id="element-643">
                  Sending you an email notification for significant changes
                </li>
              </ul>
              <p className="mb-4" data-id="element-644">
                Your continued use of the Service after changes take effect
                constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>
            <section className="mb-8" data-id="element-645">
              <h2 className="text-2xl font-bold text-burgundy mb-4" data-id="element-646">
                13. Contact Us
              </h2>
              <p className="mb-4" data-id="element-647">
                If you have any questions about this Privacy Policy or our
                privacy practices, please contact us at:
              </p>
              <p className="mb-2" data-id="element-648">Email: privacy[at]getchefchat.com</p>
              <p className="mb-4 mt-4" data-id="element-649">
                We're committed to addressing your concerns and protecting your
                privacy. Your trust is essential to our mission of helping
                restaurant owners maintain authentic customer relationships at
                digital scale.
              </p>
            </section>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="p-4 bg-white border-t border-gray-200 mt-auto" data-id="element-650">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-sage" data-id="element-651">
          <p data-id="element-652">© 2024 ChefChat. All rights reserved.</p>
        </div>
      </footer>
    </div>;
};
export default Privacy;